import LocalizedStrings from "react-localization";
import { useContext, useEffect, useRef, useState } from "react";
import { Button, Flex, Grid, VisuallyHidden } from "@aws-amplify/ui-react";
import { API, graphqlOperation, Storage } from "aws-amplify";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import toast from "react-hot-toast";

import LoaderFullScreen from "components/utils/LoaderFullScreen";
import { uploadEquipments } from "graphql/mutations";
import { LayoutContext } from "layout/context/LayoutContext";
import UploadEquipmentLog from "components/equipments/UploadEquipmentLog";
import { uploadEquipmentLogsByCompanyID } from "graphql/queries";

const EquipmentsUpload = () => {
  const { me } = useContext(LayoutContext);
  const [uploadEquipmentLogs, setUploadEquipmentLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const hiddenInput = useRef(null);

  const downloadHeadersCSV = () => {
    const headers = [
      "nombre",
      "descripcion",
      "nit_empresa",
      "sede",
      "dispositivo",
      "rutina",
      "tipo_equipo",
      "coords_lat",
      "coords_lng",
    ];
    const csvContent = headers.join(",") + "\n";
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.setAttribute("href", url);
    link.setAttribute("download", "Formato_Carga_Masiva.csv");

    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const onUploadEquipments = async (event) => {
    try {
      setLoading(true);
      const { files } = event.target;
      if (!files || files.length === 0) {
        return;
      }

      const fileSrc = `${files[0].name
        .split(".")
        .slice(0, -1)
        .join(".")}_${Date.now()}.${files[0].name.split(".").slice(-1)[0]}`;

      await Storage.put(`file-equipments-temp/${fileSrc}`, files[0], {
        contentType: files[0].type,
        level: "public",
      });

      const res = await API.graphql(
        graphqlOperation(uploadEquipments, {
          input: {
            key: fileSrc,
            companyID: me.companyID,
          },
        })
      );

      if (res.data.uploadEquipments?.success) {
        toast.success(
          res.data.uploadEquipments?.message || strings.responses.successUpload
        );
        fetchUploadEquipmentsLogs();
      } else {
        toast.error(
          res.data.uploadEquipments?.error || strings.responses.unexpectedError
        );
      }
    } catch (err) {
      console.log("Error upload equipments:", err);
      toast.error(strings.responses.unexpectedError);
    } finally {
      setLoading(false);
      hiddenInput.current.value = null;
    }
  };

  async function fetchUploadEquipmentsLogs() {
    try {
      if (!me.companyID) return;
      setLoading(true);

      const uploadEquipmentLogs = await API.graphql(
        graphqlOperation(uploadEquipmentLogsByCompanyID, {
          companyID: me.companyID,
          sortDirection: "DESC",
        })
      );
      const uploadEquipmentLogsItems =
        uploadEquipmentLogs.data.uploadEquipmentLogsByCompanyID.items;
      setUploadEquipmentLogs(uploadEquipmentLogsItems);
    } catch (err) {
      toast.error(strings.responses.errorFetch);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchUploadEquipmentsLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Grid gap="15px">
        <Flex justifyContent="space-between">
          <Button variation="link" onClick={fetchUploadEquipmentsLogs}>
            <AutorenewIcon /> {strings.refresh}
          </Button>
          <Flex justifyContent="flex-end">
            <Button
              children={strings.downloadFormat}
              variation="link"
              onClick={downloadHeadersCSV}
            />
            <Button
              className=""
              children={strings.massiveUpload}
              variation="primary"
              onClick={() => hiddenInput.current.click()}
            />
            <VisuallyHidden>
              <input
                type="file"
                tabIndex={-1}
                ref={hiddenInput}
                onChange={onUploadEquipments}
                accept={".csv"}
              />
            </VisuallyHidden>
          </Flex>
        </Flex>
        {uploadEquipmentLogs?.map((uploadEquipmentLog) => (
          <UploadEquipmentLog item={uploadEquipmentLog} />
        ))}
      </Grid>
      {loading && <LoaderFullScreen />}
    </>
  );
};

const strings = new LocalizedStrings({
  es: {
    exportCV: "Exportar Hoja de Vida",
    successExported: "Orden de Servicio exportada con éxito",
    executedServiceOrders: "Órdenes de Servicio ejecutadas",
    massiveUpload: "Cargar Equipos",
    downloadFormat: "Descargar formato CSV",
    responses: {
      unexpectedError: "Ha ocurrido un error inesperado",
      errorFetchEquipment: "Error al consultar el equipo",
      errorFetch: "Error al consultar datos",
      successUpload: "Se ha cargado la información con éxito",
    },
    refresh: "Refrescar",
  },
});

export default EquipmentsUpload;
